import { useState, useCallback } from 'react';
import studentsService from '../services/studentsService';
import { getCurrentYear } from '../utils/eventStatus';

export const useStudentsManagement = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    from: 0,
    to: 0,
    currentPage: 1,
    lastPage: 1
  });
  const [filters, setFilters] = useState({
    search: '',
    eventId: 'all',
    status: 'all',
    accessCode: '',
    year: getCurrentYear()
  });

  // Función para aplicar filtros
  const applyFilters = useCallback((studentList, currentFilters) => {
    return studentList.filter(student => {
      // Filtro por nombre (búsqueda)
      if (currentFilters.search && !student.name.toLowerCase().includes(currentFilters.search.toLowerCase())) {
        return false;
      }
      
      // Filtro por evento (a través de access codes)
      if (currentFilters.eventId !== 'all') {
        const hasAccessCodeForEvent = student.accessCodes && 
          student.accessCodes.some(code => code.eventId === currentFilters.eventId);
        if (!hasAccessCodeForEvent) {
          return false;
        }
      }
      
      // Filtro por estado
      if (currentFilters.status !== 'all') {
        const isActive = currentFilters.status === 'active';
        if (student.isActive !== isActive) {
          return false;
        }
      }
      
      // Filtro por código de acceso
      if (currentFilters.accessCode) {
        const hasMatchingCode = student.accessCodes && 
          student.accessCodes.some(code => 
            code.code.toLowerCase().includes(currentFilters.accessCode.toLowerCase())
          );
        if (!hasMatchingCode) {
          return false;
        }
      }
      
      return true;
    });
  }, []);

  // Obtener todos los Alumnos
  const fetchStudents = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await studentsService.getStudents({
        ...filters,
        ...params,
        page: pagination.page,
        limit: pagination.limit
      });
      
      // Si la respuesta incluye paginación, usarla; si no, crear paginación local
      if (response.pagination) {
        setStudents(response.data || response);
        setFilteredStudents(response.data || response);
        setPagination(response.pagination);
      } else {
        const data = response.data || response;
        setStudents(data);
        setFilteredStudents(applyFilters(data, filters));
        
        // Crear paginación local
        const total = data.length;
        const totalPages = Math.ceil(total / pagination.limit);
        const from = (pagination.page - 1) * pagination.limit + 1;
        const to = Math.min(pagination.page * pagination.limit, total);
        
        setPagination(prev => ({
          ...prev,
          total,
          totalPages,
          from,
          to
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, applyFilters]);

  // Actualizar paginación
  const updatePagination = useCallback((newPagination) => {
    setPagination(prev => ({
      ...prev,
      ...newPagination
    }));
  }, []);

  // Cambiar página
  const changePage = useCallback((newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage,
      currentPage: newPage
    }));
  }, []);

  // Buscar Alumnos por nombre
  const searchStudentsByName = useCallback(async (searchTerm) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await studentsService.searchStudents(searchTerm);
      setStudents(data);
      setFilteredStudents(applyFilters(data, filters));
      return data;
    } catch (err) {
      setError(err.message);
      setStudents([]);
      setFilteredStudents([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [filters, applyFilters]);

  // Obtener Alumnos por evento
  const fetchStudentsByEvent = useCallback(async (eventId) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await studentsService.getStudentsByEvent(eventId);
      setStudents(data);
      setFilteredStudents(data);
      return data;
    } catch (err) {
      setError(err.message);
      setStudents([]);
      setFilteredStudents([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Aplicar filtros
  const applyStudentFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    // Solo aplicar filtros si no es solo un cambio en search
    if (!newFilters.applyNow && newFilters.search !== undefined) {
      // Si solo cambió el search, no aplicar filtros automáticamente
      return;
    }
    // Aplicar filtros a la lista actual
    setFilteredStudents(applyFilters(students, newFilters));
  }, [students, applyFilters]);

  // Aplicar filtros inmediatamente
  const applyFiltersNow = useCallback(async (newFilters = null) => {
    // Resetear a la primera página cuando se aplican filtros
    setPagination(prev => ({
      ...prev,
      page: 1,
      currentPage: 1
    }));
    
    // Usar los filtros pasados como parámetro o los del estado
    const filtersToUse = newFilters || filters;
    
    // Hacer llamada al API con los filtros correctos
    const response = await studentsService.getStudents({
      ...filtersToUse,
      page: 1,
      limit: pagination.limit
    });
    
    // Procesar la respuesta
    if (response.pagination) {
      setStudents(response.data || response);
      setFilteredStudents(response.data || response);
      setPagination(response.pagination);
    } else {
      const data = response.data || response;
      setStudents(data);
      setFilteredStudents(applyFilters(data, filtersToUse));
      
      // Crear paginación local
      const total = data.length;
      const totalPages = Math.ceil(total / pagination.limit);
      const from = 1;
      const to = Math.min(pagination.limit, total);
      
      setPagination(prev => ({
        ...prev,
        total,
        totalPages,
        from,
        to,
        page: 1,
        currentPage: 1
      }));
    }
  }, [filters, pagination.limit, applyFilters]);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    const clearedFilters = {
      search: '',
      eventId: 'all',
      status: 'all',
      accessCode: '',
      year: getCurrentYear()
    };
    setFilters(clearedFilters);
    setFilteredStudents(applyFilters(students, clearedFilters));
  }, [students, applyFilters]);

  // Crear Alumno
  const createStudent = useCallback(async (studentData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newStudent = await studentsService.createStudent(studentData);
      await fetchStudents(); // Recargar la lista
      return newStudent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStudents]);

  // Actualizar Alumno
  const updateStudent = useCallback(async (id, studentData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedStudent = await studentsService.updateStudent(id, studentData);
      await fetchStudents(); // Recargar la lista
      return updatedStudent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStudents]);

  // Eliminar Alumno
  const deleteStudent = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await studentsService.deleteStudent(id);
      await fetchStudents(); // Recargar la lista
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStudents]);

  // Importar Alumnos desde Excel
  const importStudentsFromExcel = useCallback(async (formData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await studentsService.importStudentsFromExcel(formData);
      await fetchStudents(); // Recargar la lista
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStudents]);

  // Exportar Alumnos
  const exportStudents = useCallback(async (exportFilters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      await studentsService.exportStudents(exportFilters);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Descargar template de Excel
  const downloadTemplate = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await studentsService.downloadTemplate();
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Seleccionar Alumno
  const selectStudent = useCallback((student) => {
    setSelectedStudent(student);
  }, []);

  // Limpiar selección
  const clearSelection = useCallback(() => {
    setSelectedStudent(null);
  }, []);

  // Limpiar error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Estado
    students: filteredStudents,
    allStudents: students,
    selectedStudent,
    loading,
    error,
    filters,
    pagination,

    // Acciones de datos
    fetchStudents,
    updatePagination,
    changePage,
    searchStudentsByName,
    fetchStudentsByEvent,
    createStudent,
    updateStudent,
    deleteStudent,
    importStudentsFromExcel,
    exportStudents,
    downloadTemplate,

    // Acciones de filtros
    applyStudentFilters,
    applyFiltersNow,
    clearFilters,

    // Acciones de selección
    selectStudent,
    clearSelection,

    // Acciones de estado
    clearError,
  };
};